import { useMemo } from 'react';
import { storage } from '../utils/Const.utils';
import { StorageKeys } from '../types/enums/Storage.enum';
import { RolEnum } from '../types/enums/TipoRoles.enum';
import { obtenerTipoUsuario } from '../utils/helpers';
import type { PerfilEntidad } from '../types/interfaces/user.interface';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook personalizado para verificar permisos del usuario basado en su rol
 * Utiliza el helper obtenerTipoUsuario para determinar el tipo de usuario y sus permisos
 * 
 * @returns {Object} Objeto con información de permisos del usuario
 * @property {PerfilConsultaUsuario | null} perfilUsuario - Perfil del usuario con tipo de consulta
 * @property {boolean} isAdministrador - Indica si el usuario tiene rol de Administrador
 * @property {boolean} canAccessEquipo - Indica si el usuario puede acceder al módulo de Equipo
 * @property {boolean} canAsociarCaso - Indica si el usuario puede asociar miembros a un caso
 * @property {boolean} canAsociarCasoPJ - Indica si el usuario puede asociar miembros a un caso - todo usuario que se loguee como persona juridica (entidad) podra asociar casos
 */
export const useUserPermissions = () => {
  const { state } = useAuth();
  
  const perfilUsuario = useMemo(() => {
    // Obtener el perfil del storage
    const perfil = storage.get(StorageKeys.PERFIL) as PerfilEntidad;
    const idUsuario = state.user?.id;

    if (!perfil || !idUsuario) {
      return null;
    }

    return obtenerTipoUsuario(perfil, Number(idUsuario));
  }, [state.user?.id]);

  const isAdministrador = useMemo(() => {
    return perfilUsuario?.idRol === RolEnum.Administrador;
  }, [perfilUsuario]);

  const canAccessEquipo = isAdministrador;
  const canAsociarCaso = isAdministrador;
  const canAsociarCasoPJ = perfilUsuario?.idEntidad ? true : false;

  return {
    perfilUsuario,
    isAdministrador,
    canAccessEquipo,
    canAsociarCaso,
    canAsociarCasoPJ
  };
};
